/**
 * Categorizes errors so the logging layer can auto-downgrade expected
 * conditions (logout, transient connectivity loss) from ERROR to WARNING.
 * Without this, Cloud Error Reporting fills up with noise from users whose
 * tab lost wifi or who just signed out — drowning out real bugs.
 *
 * Mirrors the policy in aligned's `error_category.dart` so error grouping
 * looks the same across both apps.
 */

export type ErrorCategoryType = 'network' | 'auth' | 'data' | 'widget' | 'unknown';

export interface ErrorCategory {
  type: ErrorCategoryType;
  userMessage: string;
}

const NETWORK_FIREBASE_CODES = new Set([
  'unavailable',
  'deadline-exceeded',
  'network-request-failed',
  'cancelled',
  'internal',
]);

const AUTH_FIREBASE_CODES = new Set(['permission-denied', 'unauthenticated']);

const NETWORK_MESSAGE_PATTERNS = [
  'failed to fetch',
  'networkerror',
  'unavailableerror',
  'network request failed',
  'load failed',
  'client is offline',
];

function looksLikeFirebaseError(error: unknown): error is { code: string; name?: string } {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: unknown; name?: unknown };
  return typeof e.code === 'string';
}

function firebaseStoragePluginError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const code = (error as { code?: string }).code;
  return typeof code === 'string' && code.startsWith('storage/');
}

// Firebase Storage returns these codes when a resumable upload cannot complete
// against the backend. `storage/unknown` and `storage/retry-limit-exceeded`
// indicate the SDK exhausted its own retries against transient network/server
// conditions; `storage/canceled` fires when the caller aborts the upload. None
// are code bugs — they are the storage equivalent of `unavailable` /
// `auth/network-request-failed` and should stay out of Error Reporting.
const STORAGE_NETWORK_CODES = new Set([
  'storage/unknown',
  'storage/retry-limit-exceeded',
  'storage/canceled',
]);

function isStorageNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const code = (error as { code?: string }).code;
  return typeof code === 'string' && STORAGE_NETWORK_CODES.has(code);
}

// Firebase callable errors prefix codes with `functions/` (e.g.
// `functions/deadline-exceeded`); Firestore uses bare codes.
function stripCallablePrefix(code: string): string {
  return code.startsWith('functions/') ? code.slice('functions/'.length) : code;
}

function isNetworkError(error: unknown): boolean {
  if (looksLikeFirebaseError(error) && NETWORK_FIREBASE_CODES.has(stripCallablePrefix(error.code))) {
    return true;
  }
  const message = errorMessageString(error).toLowerCase();
  return NETWORK_MESSAGE_PATTERNS.some((p) => message.includes(p));
}

function isAuthError(error: unknown): boolean {
  // Storage rule failures (`storage/unauthorized`) carry an auth-shaped code
  // but are real bugs — we want them to stay at ERROR.
  if (firebaseStoragePluginError(error)) return false;
  if (looksLikeFirebaseError(error)) {
    const code = stripCallablePrefix(error.code);
    if (AUTH_FIREBASE_CODES.has(code)) return true;
    if (code.startsWith('auth/')) return true;
  }
  return false;
}

function isDataError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (error instanceof SyntaxError) return true;
  if (typeof RangeError !== 'undefined' && error instanceof RangeError) return true;
  return false;
}

function isWidgetError(error: unknown): boolean {
  const message = errorMessageString(error).toLowerCase();
  return (
    message.includes('hydration')
    || (message.includes('react') && message.includes('render'))
  );
}

function errorMessageString(error: unknown): string {
  if (error == null) return '';
  if (error instanceof Error) return error.message || '';
  if (typeof error === 'string') return error;
  try {
    return String(error);
  } catch {
    return '';
  }
}

export function categorizeError(error: unknown): ErrorCategory {
  if (isNetworkError(error)) {
    return {
      type: 'network',
      userMessage: 'Connection issue. Please check your internet and try again.',
    };
  }
  if (isAuthError(error)) {
    return {
      type: 'auth',
      userMessage: 'Authentication error. Please sign in again.',
    };
  }
  if (isDataError(error)) {
    return {
      type: 'data',
      userMessage: 'Unable to load data. Please refresh the page.',
    };
  }
  if (isWidgetError(error)) {
    return {
      type: 'widget',
      userMessage: 'Unable to display this content. Please refresh the page.',
    };
  }
  return {
    type: 'unknown',
    userMessage: 'Something went wrong. Please refresh the page.',
  };
}

const CLAIM_FLOW_ERROR_CODES = new Set([
  'first-approval-required-claim',
  'active-task-type-claim',
  'claiming-paused',
]);

// HttpsError('resource-exhausted', ...) details.policy values thrown by
// functions/domains/llm/policy.js. Each represents a user-actionable product
// policy rejection (input too expensive, minute rate reached, daily budget
// spent) — the message is surfaced to the user with a clear next step. Not
// code bugs; keep them out of Error Reporting.
const LLM_POLICY_KEYS = new Set([
  'per-call-estimate',
  'per-user-rate',
  'daily-user-budget',
]);

function isLlmPolicyRejection(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: unknown; details?: unknown };
  if (typeof e.code !== 'string') return false;
  if (stripCallablePrefix(e.code) !== 'resource-exhausted') return false;
  const details = e.details as { policy?: unknown } | undefined;
  return typeof details?.policy === 'string' && LLM_POLICY_KEYS.has(details.policy);
}

const CLAIM_FLOW_ERROR_NAMES = new Set([
  'FirstApprovalRequiredClaimError',
  'ActiveTaskTypeClaimError',
  'ClaimingPausedError',
  // The user has typed/pasted more data than Firestore can hold (1 MiB cap).
  // Surfaced to the user with an actionable message; not a code bug.
  'ResultPayloadTooLargeError',
]);

function isStorageQuotaError(error: unknown): boolean {
  if (typeof DOMException !== 'undefined'
    && error instanceof DOMException
    && error.name === 'QuotaExceededError') return true;
  const message = errorMessageString(error).toLowerCase();
  return message.includes('quota') && message.includes('exceeded');
}

// Browsers throw this when the IndexedDB connection a Firebase SDK is using
// is torn down out from under it (OS memory pressure, cross-tab invalidation,
// aggressive storage reclamation, in-progress upgrade). The error message
// itself ends with "Refresh the page to try again" because recovery requires
// reopening every IDB connection — nothing the app can do in-process. Keep
// the entry in Logs Explorer at WARNING, but out of Error Reporting.
function isIndexedDbConnectionLost(error: unknown): boolean {
  const message = errorMessageString(error).toLowerCase();
  return message.includes('connection to indexed database server lost');
}

// JSZip throws generic Errors whose messages identify a non-zip / corrupted
// input file. Task types (comp-workflows-drug-discovery, computational-
// workflow-docker) parse user-uploaded submissions; when the user picks the
// wrong file, the message is already shown to them via setError. These are
// user input errors, not code bugs, so keep them out of Error Reporting.
const ZIP_PARSE_MESSAGE_PATTERNS = [
  "can't find end of central directory",
  'corrupted zip',
  'invalid signature',
];

function isZipParseError(error: unknown): boolean {
  const message = errorMessageString(error).toLowerCase();
  return ZIP_PARSE_MESSAGE_PATTERNS.some((p) => message.includes(p));
}

// The server rejects LLM calls whose estimated cost exceeds a per-call budget
// with `HttpsError('resource-exhausted', ..., { policy: 'per-call-estimate' })`
// — see functions/domains/llm/policy.js. That's a user-input rejection: the
// expert's prompt or attachments are too large. The UI already displays the
// message to the user; it should not fill up Error Reporting.
function isRequestBudgetError(error: unknown): boolean {
  const message = errorMessageString(error).toLowerCase();
  return message.includes('per-call budget');
}

function isClaimFlowError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const code = (error as { code?: string }).code;
  if (typeof code === 'string' && CLAIM_FLOW_ERROR_CODES.has(code)) return true;
  const name = (error as { name?: string }).name;
  return typeof name === 'string' && CLAIM_FLOW_ERROR_NAMES.has(name);
}

/**
 * True when the error represents an expected condition (logout, transient
 * connectivity loss, user-flow claim gates) and should be logged as WARNING
 * rather than ERROR.
 */
export function shouldDowngradeToWarning(error: unknown): boolean {
  if (isClaimFlowError(error)) return true;
  if (isLlmPolicyRejection(error)) return true;
  if (isStorageQuotaError(error)) return true;
  if (isZipParseError(error)) return true;
  if (isStorageNetworkError(error)) return true;
  if (isRequestBudgetError(error)) return true;
  if (isIndexedDbConnectionLost(error)) return true;
  const category = categorizeError(error);
  return category.type === 'network' || category.type === 'auth';
}
